import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  setDoc,
} from "firebase/firestore";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, secondaryAuth, db } from "../../database/firebase";

// Fetch all users from Firestore
export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (_, { rejectWithValue }) => {
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return users;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch user details from Firestore
export const fetchUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async (uid, { rejectWithValue }) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        return { uid, ...userDoc.data() };
      } else {
        return rejectWithValue("User not found");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create new user
export const createUser = createAsyncThunk(
  "user/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const {
        name,
        mobile,
        whatsappNumber,
        address,
        email,
        password,
        role,
        purpose,
        referredBy,
        sponsoredBy,
        referralInput,
        herbalifeId,
        preferredStatus,
        herbalifeLastName,
        joinYear,
        herbalifeEmail,
        herbalifePassword,
        sponsorName,
        sponsorLastName,
        sponsorId,
        weight,
        bmi,
        bodyFat,
        familyDetails,
      } = userData;

      // Generate a temporary email if not provided
      const userEmail = email || `${mobile}@temp.wellhub.com`;

      // Create user in Firebase Auth using SECONDARY auth instance
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        userEmail,
        password
      );
      const user = userCredential.user;

      // Create simplified user document structure
      const completeUserData = {
        userId: user.uid,
        personalInformation: {
          fullName: name || "",
          mobileNumber: mobile || "",
          whatsappNumber: whatsappNumber || mobile || "",
          email: email || "",
          address: address || ""
        },
        accountSecurity: {
          accountRole: role || "User",
          detailedPurpose: purpose || "",
          referredBy: referredBy || "",
          sponsoredBy: sponsoredBy || "",
          referralInput: referralInput || ""
        },
        herbalifeDetails: {
          herbalifeId: herbalifeId || "",
          preferredStatus: preferredStatus || "",
          herbalifeLastName: herbalifeLastName || "",
          joinYear: joinYear || "",
          herbalifeEmail: herbalifeEmail || "",
          herbalifePassword: herbalifePassword || "",
          sponsorName: sponsorName || "",
          sponsorLastName: sponsorLastName || "",
          sponsorId: sponsorId || "",
          totalLifetimeVP: 0
        },
        familyDetails: {
          birthday: familyDetails?.birthday || '',
          maritalStatus: familyDetails?.maritalStatus || '',
          spouseName: familyDetails?.spouseName || '',
          marriageDate: familyDetails?.marriageDate || '',
          children: familyDetails?.children || []
        },
        inventory: {
          remainingShakes: 0,
          lastTopUpDate: new Date().toISOString()
        },
        currentHealthMetrics: {
          lastWeight: parseFloat(weight) || 0,
          lastBMI: parseFloat(bmi) || 0,
          lastBodyFat: parseFloat(bodyFat) || 0,
          lastUpdated: new Date().toISOString()
        },
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isOnboardingComplete: true,
          isVerified: true
        }
      };

      // Save to Firestore
      await setDoc(doc(db, "users", user.uid), completeUserData);

      // Sign out from secondary auth to prevent session conflicts
      await signOut(secondaryAuth);

      return { id: user.uid, ...completeUserData };
    } catch (error) {
      // Make sure to sign out from secondary auth even on error
      try {
        await signOut(secondaryAuth);
      } catch (signOutError) {
        console.error("Error signing out from secondary auth:", signOutError);
      }
      return rejectWithValue(error.message);
    }
  }
);

// Update user profile
export const updateUserProfile = createAsyncThunk(
  "user/updateProfile",
  async ({ uid, updates }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, "users", uid);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(userRef, updateData);
      return { uid, ...updateData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    users: [],
    currentUser: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearUserError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch user details
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
        state.error = null;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Create user
    builder
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.unshift(action.payload);
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = { ...state.currentUser, ...action.payload };
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentUser, clearCurrentUser, clearUserError } =
  userSlice.actions;
export default userSlice.reducer;
